import { useEffect, useState, useRef } from 'react';

const generateId = () => Math.random().toString(36).substr(2, 9);

export const useMultiTab = () => {
    // We assume we are master until proven otherwise. This avoids flickering on first load.
    const [isMaster, setIsMaster] = useState(true);
    const isMasterRef = useRef(true);
    const [tabId] = useState(() => generateId());

    useEffect(() => {
        let isClosed = false;
        let electionTimer: any = null;
        let delayTimer: any = null;

        let channel: BroadcastChannel | null = null;
        try {
            channel = new BroadcastChannel('smartsolution_tab_sync');
        } catch (e) {
            console.warn('BroadcastChannel not supported or failed to initialize', e);
            return;
        }

        const safePostMessage = (data: any) => {
            if (isClosed || !channel) return;
            try {
                channel.postMessage(data);
            } catch (err) {
                // Ignore error if channel was closed concurrently
            }
        };

        const setMaster = (masterStatus: boolean) => {
            setIsMaster(masterStatus);
            isMasterRef.current = masterStatus;
        };

        // On load, ask who is master
        safePostMessage({ type: 'WHO_IS_MASTER', senderId: tabId });

        // Wait 300ms. If nobody says they are master, I become master.
        electionTimer = setTimeout(() => {
            if (!isMasterRef.current) {
                setMaster(true);
            }
            // Announce that I am master
            safePostMessage({ type: 'I_AM_MASTER', senderId: tabId });
        }, 300);

        channel.onmessage = (event) => {
            if (isClosed) return;
            const { type, senderId } = event.data;

            if (type === 'WHO_IS_MASTER') {
                // If someone asks who is master, and I am master, I reply
                if (isMasterRef.current) {
                    safePostMessage({ type: 'I_AM_MASTER', senderId: tabId });
                }
            } else if (type === 'I_AM_MASTER') {
                // Someone else is master. I yield and become slave.
                if (senderId !== tabId) {
                    if (electionTimer) clearTimeout(electionTimer);
                    setMaster(false);
                }
            } else if (type === 'MASTER_CLOSING') {
                // The master is closing! Start a new election!
                setMaster(false);
                if (delayTimer) clearTimeout(delayTimer);
                delayTimer = setTimeout(() => {
                    if (isClosed) return;
                    safePostMessage({ type: 'WHO_IS_MASTER', senderId: tabId });
                    if (electionTimer) clearTimeout(electionTimer);
                    electionTimer = setTimeout(() => {
                        if (isClosed) return;
                        if (!isMasterRef.current) {
                            setMaster(true);
                            safePostMessage({ type: 'I_AM_MASTER', senderId: tabId });
                        }
                    }, 300);
                }, Math.random() * 200);
            }
        };

        const handleUnload = () => {
            if (isMasterRef.current) {
                safePostMessage({ type: 'MASTER_CLOSING', senderId: tabId });
            }
            isClosed = true;
            if (channel) {
                try {
                    channel.close();
                } catch (e) {}
            }
        };

        window.addEventListener('beforeunload', handleUnload);

        return () => {
            isClosed = true;
            if (electionTimer) clearTimeout(electionTimer);
            if (delayTimer) clearTimeout(delayTimer);
            window.removeEventListener('beforeunload', handleUnload);
            if (channel) {
                try {
                    channel.close();
                } catch (e) {}
            }
        };
    }, [tabId]);

    return { isMaster };
};
